// Coluna de conversas do Chat Interno (mockup Aeros): título + ações, busca,
// pills Todas / Não lidas / Grupos e cards. A leitura (POST /chats/:id/read)
// continua acontecendo ao abrir a conversa.
import React, { useContext, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import GroupIcon from "@material-ui/icons/Group";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { FILTERS, filterChats, filterCounts, matchesSearch, chatTitle, unreadOf, lastPreview, timeLabel, isGroup, otherUserOf, initialsOf, avatarColorFor, isMuted } from "./chatRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const purpleText = stateText(theme, "#C08BFF", "purple");
  return {
    root: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 },
    head: { padding: "16px 16px 0", flexShrink: 0 },
    titleRow: { display: "flex", alignItems: "center", marginBottom: 12 },
    title: { fontFamily: AEROS.fontDisplay, fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary },
    actions: { marginLeft: "auto", display: "flex", gap: 6 },
    iconBtn: { width: 32, height: 32, borderRadius: 9, border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`, color: theme.palette.text.secondary, padding: 0, "& .MuiSvgIcon-root": { fontSize: 17 } },
    newBtn: { height: 32, borderRadius: 9, padding: "0 12px", fontSize: 12.5, fontWeight: 700, "& .MuiButton-startIcon": { marginRight: 4 }, "& .MuiSvgIcon-root": { fontSize: 16 } },
    search: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "4px 12px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      "& .MuiSvgIcon-root": { fontSize: 17, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    pills: { display: "flex", gap: 6, marginTop: 12 },
    pill: { borderRadius: 99, padding: "4px 12px", fontSize: 11.5, fontWeight: 600, color: theme.palette.text.secondary, border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`, background: "none", cursor: "pointer", font: "inherit", "& span": { opacity: 0.7, marginLeft: 3 } },
    pillOn: { color: purpleText, backgroundColor: "rgba(160,68,255,0.15)", borderColor: "transparent", "& span": { opacity: 0.7 } },
    pillUnread: { "& span": { color: stateText(theme, AEROS.cyan, "info"), opacity: 1 } },
    list: { flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 10px", display: "flex", flexDirection: "column", gap: 4, ...theme.scrollbarStyles },
    card: { display: "flex", gap: 11, padding: "10px 12px", borderRadius: 12, border: "1px solid transparent", position: "relative", cursor: "pointer", textAlign: "left", background: "none", width: "100%", font: "inherit", color: "inherit", "&:hover": { backgroundColor: "rgba(160,68,255,0.06)" } },
    cardActive: { backgroundColor: "rgba(160,68,255,0.08)", borderColor: "rgba(160,68,255,0.3)" },
    activeBar: { position: "absolute", left: 0, top: 10, bottom: 10, width: 3, borderRadius: 3, background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${AEROS.cyan})` },
    avatarWrap: { position: "relative", flex: "none" },
    groupAvatar: { width: 42, height: 42, borderRadius: 12, backgroundColor: "rgba(160,68,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: purpleText, overflow: "hidden", "& .MuiSvgIcon-root": { fontSize: 20 }, "& img": { width: "100%", height: "100%", objectFit: "cover" } },
    dmAvatar: { width: 42, height: 42, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#07080D", overflow: "hidden", "& img": { width: "100%", height: "100%", objectFit: "cover" } },
    dot: { position: "absolute", bottom: 0, right: 0, width: 10, height: 10, borderRadius: "50%", boxShadow: `0 0 0 2px ${theme.palette.aeros.background}` },
    body: { flex: 1, minWidth: 0 },
    row1: { display: "flex", alignItems: "baseline", gap: 6 },
    name: { fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: theme.palette.text.primary },
    nameUnread: { fontWeight: 700 },
    time: { marginLeft: "auto", fontSize: 10.5, color: theme.palette.aeros.textTertiary, flex: "none", fontVariantNumeric: "tabular-nums" },
    timeUnread: { color: stateText(theme, AEROS.cyan, "info"), fontWeight: 700 },
    row2: { display: "flex", alignItems: "center", gap: 6, marginTop: 3 },
    last: { fontSize: 12, color: theme.palette.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 },
    lastUnread: { color: theme.palette.text.primary },
    badge: { background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`, color: "#07080D", fontSize: 10, fontWeight: 800, borderRadius: 99, minWidth: 18, height: 18, padding: "0 5px", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" },
    muted: { fontSize: 14, color: theme.palette.aeros.textTertiary },
    empty: { padding: "36px 12px", textAlign: "center", fontSize: 12.5, color: theme.palette.aeros.textTertiary },
  };
});

export const profileImageUrl = (u) => {
  if (!u?.profileImage) return null;
  return u.profileImage.startsWith("http") ? u.profileImage : `${getBackendUrl()}/public/company${u.companyId}/user/${u.profileImage}`;
};

export const groupImageUrl = (chat) => {
  if (!chat?.groupImage) return null;
  return chat.groupImage.startsWith("http") ? chat.groupImage : `${getBackendUrl()}${chat.groupImage}`;
};

export default function ChatList({ chats, loading, handleSelectChat, onlineUsers, onNew, onMarkAllRead }) {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { user: me } = useContext(AuthContext);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const online = useMemo(() => new Set((onlineUsers || []).filter((u) => u.online).map((u) => Number(u.id))), [onlineUsers]);
  const counts = useMemo(() => filterCounts(chats, me.id), [chats, me.id]);
  const shown = useMemo(
    () =>
      filterChats(chats, filter, me.id)
        .filter((c) => matchesSearch(c, search, me.id))
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    [chats, filter, search, me.id]
  );

  const open = async (chat) => {
    if (unreadOf(chat, me.id) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: me.id });
      } catch (err) {
        // leitura falha silenciosa, a conversa abre mesmo assim
      }
    }
    handleSelectChat(chat);
    if (id !== chat.uuid) history.push(`/chats/${chat.uuid}`);
  };

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <div className={classes.titleRow}>
          <div className={classes.title}>{i18n.t("chatIndex.title")}</div>
          <div className={classes.actions}>
            <Tooltip title={i18n.t("chatIndex.markAllRead")}>
              <span>
                <IconButton className={classes.iconBtn} onClick={onMarkAllRead} disabled={counts.unread === 0} aria-label={i18n.t("chatIndex.markAllRead")}>
                  <DoneAllIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" color="primary" className={classes.newBtn} startIcon={<AddIcon />} onClick={onNew}>
              {i18n.t("chatIndex.new")}
            </Button>
          </div>
        </div>
        <InputBase className={classes.search} placeholder={i18n.t("chatIndex.search")} value={search} onChange={(e) => setSearch(e.target.value)} startAdornment={<SearchIcon />} inputProps={{ "aria-label": i18n.t("chatIndex.search") }} />
        <div className={classes.pills} role="tablist">
          {FILTERS.map((f) => (
            <button key={f} type="button" role="tab" aria-selected={filter === f} className={`${classes.pill} ${filter === f ? classes.pillOn : ""} ${f === "unread" && filter !== f ? classes.pillUnread : ""}`} onClick={() => setFilter(f)}>
              {i18n.t(`chatIndex.filters.${f}`)}
              {f !== "groups" && <span>{counts[f]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.list}>
        {loading && shown.length === 0 && (
          <div className={classes.empty}>
            <CircularProgress size={20} />
          </div>
        )}
        {!loading && shown.length === 0 && <div className={classes.empty}>{i18n.t("chatIndex.noChats")}</div>}
        {shown.map((chat) => {
          const grp = isGroup(chat);
          const other = otherUserOf(chat, me.id);
          const unread = unreadOf(chat, me.id);
          const active = chat.uuid === id;
          const isOn = other && online.has(Number(other.id));
          const img = grp ? groupImageUrl(chat) : profileImageUrl(other);
          return (
            <button type="button" key={chat.id} className={`${classes.card} ${active ? classes.cardActive : ""}`} onClick={() => open(chat)}>
              {active && <span className={classes.activeBar} />}
              <div className={classes.avatarWrap}>
                {grp ? (
                  <div className={classes.groupAvatar}>{img ? <img src={img} alt="" /> : <GroupIcon />}</div>
                ) : (
                  <>
                    <div className={classes.dmAvatar} style={{ backgroundColor: avatarColorFor(other?.id) }}>
                      {img ? <img src={img} alt="" /> : initialsOf(other?.name)}
                    </div>
                    <span className={classes.dot} style={{ backgroundColor: isOn ? AEROS.success : "#5B6376" }} />
                  </>
                )}
              </div>
              <div className={classes.body}>
                <div className={classes.row1}>
                  <span className={`${classes.name} ${unread ? classes.nameUnread : ""}`}>{chatTitle(chat, me.id)}</span>
                  <span className={`${classes.time} ${unread ? classes.timeUnread : ""}`}>{timeLabel(chat.lastMessage?.createdAt || chat.updatedAt)}</span>
                </div>
                <div className={classes.row2}>
                  <span className={`${classes.last} ${unread ? classes.lastUnread : ""}`}>{lastPreview(chat, me.id) || i18n.t("chatList.noMessages")}</span>
                  {unread > 0 && <span className={classes.badge}>{unread}</span>}
                  {isMuted(chat, me.id) && <NotificationsOffIcon className={classes.muted} />}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
